import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, ArrowRight, Eye, Users, MessageSquare, Compass, Send, MapPin, Calendar, Quote } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from 'sonner';
import { IMAGES } from '@/assets/images';
import { sampleStories, formatDate, scrollToSection, type KindnessCategory } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
const storySchema = z.object({
  author: z.string().min(2, 'Name must be at least 2 characters'),
  role: z.enum(['Patient', 'Provider', 'Staff', 'Family'] as const),
  setting: z.string().min(3, 'Setting is required'),
  location: z.string().min(2, 'Location is required'),
  content: z.string().min(10, 'Story must be at least 10 characters')
});
type StoryFormValues = z.infer<typeof storySchema>;
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  animate: {
    opacity: 1,
    y: 0
  },
  transition: {
    duration: 0.6,
    ease: [0.22, 1, 0.36, 1]
  }
};
const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
export default function Home() {
  const [joinOpen, setJoinOpen] = useState(false);
  const [joinName, setJoinName] = useState('');
  const [joinEmail, setJoinEmail] = useState('');
  const [joinSubmitting, setJoinSubmitting] = useState(false);
  function handleJoinSubmit(e: React.FormEvent) {
    e.preventDefault();
    setJoinSubmitting(true);
    const subject = encodeURIComponent('Join the KindSight Movement');
    const body = encodeURIComponent(`Name: ${joinName}\nEmail: ${joinEmail}`);
    window.location.href = `mailto:info@kind-sight.com?subject=${subject}&body=${body}`;
    toast.success('Thank you for joining the movement!');
    setJoinName('');
    setJoinEmail('');
    setJoinSubmitting(false);
    setJoinOpen(false);
  }
  const form = useForm<StoryFormValues>({
    resolver: zodResolver(storySchema),
    defaultValues: {
      author: '',
      role: 'Patient',
      setting: '',
      location: '',
      content: ''
    }
  });
  async function onSubmit(data: StoryFormValues) {
    try {
      const SUPABASE_URL = 'https://ewsizebzitjmllwylwnt.supabase.co';
      const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImV3c2l6ZWJ6aXRqbWxsd3lsd250Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzY5ODA3NDgsImV4cCI6MjA5MjU1Njc0OH0.DZ7b-7VHjxE56DB3kVOVxFI8Cfc8kvDBaVLwn-dC6OU';
      const response = await fetch(`${SUPABASE_URL}/functions/v1/send-sighting-2026_04_23`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          author: data.author,
          role: data.role,
          setting: data.setting,
          location: data.location,
          content: data.content
        })
      });
      const result = await response.json();
      if (!response.ok || result.error) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }
      toast.success('Thank you for Sighting Kindness! Your story has been received.');
      form.reset();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Submission failed: ${msg}`);
    }
  }
  return <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section id="hero" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.ABSTRACT_FLOW_1} alt="Forward Movement" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        </div>

        <div className="container relative z-10 px-4 mx-auto text-center">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          ease: "easeOut"
        }} className="mb-6 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary border border-primary/20 backdrop-blur-sm">
            <Compass className="w-4 h-4" />
            <span className="text-sm font-medium tracking-wide uppercase">A New Direction in Healthcare</span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6 tracking-tight leading-[1.1]">
            Forward Movement <br />
            <span className="text-primary">Centered in Care</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-10 leading-relaxed">KindSight is more than an initiative—it's a collective motion toward a healthcare system where every interaction is woven with kindness.</motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="rounded-full h-14 px-8 text-lg bg-accent hover:bg-accent/90 text-accent-foreground shadow-lg shadow-accent/20" onClick={() => scrollToSection('share')}>
              Sight Kindness <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button variant="outline" size="lg" className="rounded-full h-14 px-8 text-lg border-primary/20 hover:bg-primary/5" onClick={() => scrollToSection('manifesto')}>
              Read Our Manifesto
            </Button>
          </motion.div>
        </div>

        <motion.div animate={{
        y: [0, 10, 0]
      }} transition={{
        duration: 2,
        repeat: Infinity
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <div className="w-1 h-12 rounded-full bg-gradient-to-b from-primary to-transparent opacity-40" />
        </motion.div>
      </section>

      {/* Manifesto Section */}
      <section id="manifesto" className="py-24 bg-muted/30 relative overflow-hidden">
        <div className="container px-4 mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial="initial" whileInView="animate" viewport={{
            once: true
          }} variants={staggerContainer} className="relative z-10">
              <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-8 leading-tight">
                The KindSight <span className="text-secondary">Manifesto</span>
              </motion.h2>
              <motion.div variants={fadeInUp} className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  The current healthcare system has grown unintentionally unkind to those within it. From providers to patients, and everyone in between, kindness has been sidelined in the relentless pursuit of efficiency.
                </p>
                <p className="font-normal">
                  KindSight&trade; was born from this critical insight: within the vast streams of medical data and clinical protocol, the human dynamics of a team are the true live operating system. These dynamics are the primary drivers of patient outcomes, a flourishing organizational culture, and the psychological safety required for excellence.
                </p>
                <p>
                  Our mission is to <strong>Center Kindness in Care</strong> by operationalizing it as a measurable performance standard. We bridge the gap between clinical intent and objective reality through <strong>Human Performance Intelligence</strong>&mdash;quantifying the patterns of coordination and empathy that traditional surveys miss.
                </p>
                <p>
                  By &ldquo;Sighting Kindness,&rdquo; we transform invisible behavioral signals into a legible Kindness Index. We capture it, we calibrate it through our &ldquo;Digital Twin&rdquo; methodology, and we use it to drive a national benchmarking movement. Join us in making the invisible visible.
                </p>
              </motion.div>
              <motion.div variants={fadeInUp} className="mt-10">
                <div className="flex items-center gap-4 p-6 rounded-2xl bg-background border border-border shadow-sm">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                    <Eye className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-semibold">Visibility creates change</h4>
                    <p className="text-sm text-muted-foreground">What we choose to see, we choose to grow.</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 1
          }} viewport={{
            once: true
          }} className="relative aspect-square rounded-[2rem] overflow-hidden shadow-2xl">
              <img src={IMAGES.HEALTHCARE_COMPASSION_3} alt="Compassionate Care" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
              <div className="absolute bottom-8 left-8 right-8 p-6 bg-background/80 backdrop-blur-md rounded-xl border border-white/20">
                <p className="text-sm italic font-medium text-foreground">
                  "The smallest act of kindness is worth more than the grandest intention."
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stories Section */}
      <section id="stories" className="py-24 relative">
        
      </section>

      {/* Share Section */}
      <section id="share" className="py-24 bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.ABSTRACT_FLOW_3} className="w-full h-full object-cover opacity-10" alt="Form Texture" />
        </div>
        
        <div className="container relative z-10 px-4 mx-auto">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Heart className="w-12 h-12 text-accent mx-auto mb-6" />
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Sight Kindness</h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Did you witness or experience an act of kindness in a healthcare setting? 
                Sharing your story helps us build the momentum for change.
              </p>
            </div>

            <Card className="bg-white text-foreground p-8 md:p-12 rounded-3xl shadow-2xl">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <FormField control={form.control} name="author" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Your Name (or Anonymous)</FormLabel>
                          <FormControl>
                            <Input placeholder="Sarah M." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name="role" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Your Role</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select your role" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="Patient">Patient</SelectItem>
                              <SelectItem value="Provider">Provider</SelectItem>
                              <SelectItem value="Staff">Staff</SelectItem>
                              <SelectItem value="Family">Family</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>} />
                  </div>

                  <div className="grid md:grid-cols-2 gap-8">
                    <FormField control={form.control} name="setting" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Healthcare Setting</FormLabel>
                          <FormControl>
                            <Input placeholder="Hospital, Clinic, Home Care..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                    <FormField control={form.control} name="location" render={({
                    field
                  }) => <FormItem>
                          <FormLabel>Location</FormLabel>
                          <FormControl>
                            <Input placeholder="City, State" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>} />
                  </div>

                  <FormField control={form.control} name="content" render={({
                  field
                }) => <FormItem>
                        <FormLabel>Describe the Sighting</FormLabel>
                        <FormControl>
                          <Textarea placeholder="Tell us what happened..." className="min-h-[150px] resize-none" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>} />

                  <Button type="submit" className="w-full h-14 text-lg bg-accent hover:bg-accent/90 text-accent-foreground rounded-xl">
                    <Send className="w-5 h-5 mr-2" /> Submit Your Sighting
                  </Button>
                </form>
              </Form>
            </Card>
          </div>
        </div>
      </section>

      {/* Movement Section */}
      <section id="movement" className="py-24 relative overflow-hidden">
        <div className="container px-4 mx-auto">
          <div className="relative rounded-[3rem] bg-primary/5 p-12 md:p-24 overflow-hidden">
            <div className="absolute top-0 right-0 w-1/3 h-full opacity-10">
               <img src={IMAGES.ABSTRACT_FLOW_4 || IMAGES.ABSTRACT_FLOW_1} className="w-full h-full object-cover" alt="" />
            </div>
            
            <div className="relative z-10 max-w-3xl">
              <h2 className="text-4xl md:text-6xl font-bold mb-8">
                The movement is <span className="text-primary italic">forward.</span>
              </h2>
              <p className="text-xl text-muted-foreground mb-12 leading-relaxed">
                KindSight is just getting started. We are building a future where kindness is the standard of care, not the exception. Join our community to receive updates and learn how you can help center kindness in your own healthcare environment.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Button size="lg" className="rounded-full h-14 px-10 bg-primary hover:bg-primary/90" onClick={() => setJoinOpen(true)}>
                  Join the Movement
                </Button>
                
              </div>

              <div className="mt-16 grid grid-cols-2 md:grid-cols-3 gap-8">
                <div>
                  
                  
                </div>
                <div>
                  
                  
                </div>
                <div className="col-span-2 md:col-span-1">
                  
                  
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Branding */}
      <section className="py-12 border-t border-border">
        <div className="container px-4 mx-auto flex flex-col items-center">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10">
              <img src={IMAGES.KINDSIGHT_LOGO_FOR_WEB} alt="KindSight" className="w-full h-full object-contain" />
            </div>
            <span className="text-2xl font-bold tracking-tighter">KindSight</span>
          </div>
          <p className="text-muted-foreground text-sm uppercase tracking-[0.2em]">Center Kindness in Care</p>
          <div className="mt-8 flex gap-6 text-muted-foreground">
            <a href="#" className="hover:text-primary transition-colors"></a>
            <a href="#" className="hover:text-primary transition-colors"></a>
            <a href="#" className="hover:text-primary transition-colors"></a>
          </div>
          <p className="mt-12 text-xs text-muted-foreground/60">
            © 2026 KindSight Healthcare Initiative. All rights reserved.
          </p>
        </div>
      </section>
      {/* Join the Movement Dialog */}
      <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Join the Movement</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Enter your name and email and we'll be in touch about how you can help center kindness in care.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleJoinSubmit} className="mt-4 flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="join-name">Name</Label>
              <Input id="join-name" placeholder="Your full name" value={joinName} onChange={e => setJoinName(e.target.value)} required />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="join-email">Email</Label>
              <Input id="join-email" type="email" placeholder="you@example.com" value={joinEmail} onChange={e => setJoinEmail(e.target.value)} required />
            </div>
            <Button type="submit" disabled={joinSubmitting} className="mt-2 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
              Submit
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>;
}